import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import { updateResidentResource } from 'store/resources/thunks/updateResidentResource.thunk';
import * as bulletinActions from './store/actions/bulletinActions';
import { createBulletin } from './store/thunks/createBulletin';
import { createManualResource } from './store/thunks/createManualResource';
import { deleteAttachment } from './store/thunks/deleteAttachment';
import { deleteBulletin } from './store/thunks/deleteBulletin';
import { editBulletin } from './store/thunks/editBulletin';
import { loadAttachments } from './store/thunks/loadAttachments';
import { loadBulletinSendData } from './store/thunks/loadBulletinSendData';
import { loadImageSelectorData } from './store/thunks/loadImageSelectorData';
import { loadInitialBulletinData } from './store/thunks/loadInitialBulletinData';
import { loadProjectAndSubProjectBulletins } from './store/thunks/loadProjectAndSubProjectBulletins';
import { refreshBulletins } from './store/thunks/refreshBulletins';
import { sendBulletin } from './store/thunks/sendBulletin';
import { sendJoiningInstruction } from './store/thunks/sendJoiningInstruction';
import { updateManualResource } from './store/thunks/updateManualResource.thunk';

const resourcesAdapter = createEntityAdapter();
const manualResourcesAdapter = createEntityAdapter();
const bulletinsAdapter = createEntityAdapter();
const projectAdapter = createEntityAdapter();
const filePermissionsAdapter = createEntityAdapter();
const employerProjectAdapter = createEntityAdapter();
const defaultFilePermissionsAdapter = createEntityAdapter();
const attachmentsAdapter = createEntityAdapter();

const resetState = {
  resources: createEntityAdapter().getInitialState(),
  manualResources: createEntityAdapter().getInitialState(),
  bulletins: createEntityAdapter().getInitialState(),
  project: createEntityAdapter().getInitialState(),
  filePermissions: createEntityAdapter().getInitialState(),
  employerProject: createEntityAdapter().getInitialState(),
  defaultFilePermissions: createEntityAdapter().getInitialState(),
  attachments: createEntityAdapter().getInitialState(),
  filepermissionUrls: [],
};

/**
 * How long a draft waits for its board before it is dropped rather than
 * opened. An hour: long enough to finish what the user was doing when they
 * asked, short enough that a draft about "this week" is still about this week.
 */
export const PENDING_DRAFT_TTL_MS = 60 * 60 * 1000;

/**
 * Whether a draft was revised from exactly what the create editor holds now.
 *
 * `basis` is the editor text the server showed the assistant, echoed back on
 * the draft; `composing` is what the editor holds at this moment, kept
 * current by the editor itself. Equal means nothing was typed since the
 * message went out, so the draft can take the editor without losing a word.
 * No basis means the assistant saw no editor text at all — an older server,
 * or a draft asked for before the editor was open — and the draft may then be
 * a revision of something older than what is on screen. Either way the
 * answer is the same: not this editor, not silently.
 */
const revisesWhatEditorHolds = (state, draft) => {
  const { basis } = draft;
  const composing = state.bulletins.composing;

  return Boolean(
    basis &&
      composing &&
      basis.header === (composing.header ?? '') &&
      basis.content === (composing.content ?? '')
  );
};

/** The draft into the create editor, whatever the editor held. */
const takeEditor = (state, draft) => {
  state.bulletins.createBulletin = true;
  state.bulletins.bulletinDialogData = null;
  state.bulletins.draft = draft;
  state.pendingBulletinDraft = null;
};

/**
 * Put the waiting draft into the create editor, if the board can take it now.
 *
 * "Now" means the board is showing the draft's project — `project` holds
 * whichever one the scene loaded and is emptied on unmount, the same test
 * `revealBulletin` makes — and no edit dialog is open, that being another
 * bulletin's unsaved work. An open *create* editor is taken only when the
 * draft revises exactly what it holds (see `revisesWhatEditorHolds`);
 * otherwise the draft keeps waiting and the editor offers it, with the
 * user's own text left where it is. The board keys the editor on the draft,
 * so taking it remounts the form rather than swapping fields under a cursor.
 */
const openPendingDraftIfShowing = (state, projectId) => {
  const draft = state.pendingBulletinDraft;

  if (!draft || !state.project.entities[projectId]) return;
  if (state.bulletins.editBulletin) return;
  if (state.bulletins.createBulletin && !revisesWhatEditorHolds(state, draft)) {
    return;
  }

  takeEditor(state, draft);
};

const bulletinBoardSlice = createSlice({
  name: 'bulletinBoard',
  initialState: {
    resources: resourcesAdapter.getInitialState(),
    manualResources: manualResourcesAdapter.getInitialState(),
    bulletins: bulletinsAdapter.getInitialState(),
    project: projectAdapter.getInitialState(),
    filePermissions: filePermissionsAdapter.getInitialState(),
    employerProject: employerProjectAdapter.getInitialState(),
    defaultFilePermissions: defaultFilePermissionsAdapter.getInitialState(),
    attachments: attachmentsAdapter.getInitialState(),
    filepermissionUrls: [],
    selectedRecipientResourceToEditId: '',
    /**
     * A bulletin drafted elsewhere — by Nerobotti — that no editor has taken
     * yet. Outside `resetState` on purpose: it is waiting for the board to
     * show its project, which may be the next mount, not this one.
     */
    pendingBulletinDraft: null,
  },
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(loadInitialBulletinData.pending, (state) => ({
        ...state,
        loading: true,
      }))
      .addCase(loadInitialBulletinData.fulfilled, (state, action) => {
        state.loading = false;
        bulletinsAdapter.setMany(state.bulletins, action.payload.bulletins);
        projectAdapter.setMany(state.project, action.payload.project);
        const filterOutNulls = action.payload.filePermissions.filter(
          (fp) => fp !== null && fp !== undefined && fp.id
        );
        filePermissionsAdapter.setMany(state.filePermissions, filterOutNulls);
        employerProjectAdapter.setMany(
          state.employerProject,
          action.payload.employerProject
        );
        defaultFilePermissionsAdapter.setMany(
          state.defaultFilePermissions,
          action.payload.defaultFilePermissions
        );
      })
      /**
       * A background re-read, currently from Nerobotti finishing a write.
       *
       * `setMany` rather than `setAll`, and no loading flag: this runs while the
       * user is looking at the list, so it must add and update without clearing
       * the open bulletin or the dialog state that live in the same object.
       * A rejection is deliberately unhandled — the list simply stays as it was,
       * which is the right outcome for a refresh nobody asked for.
       *
       * The cost of `setMany` is that it never evicts, so a deleted row would
       * outlive the write that removed it. That is `removeBulletin`'s job
       * below, and not a reason to reach for `setAll`: this adapter is shared
       * across projects — `loadProjectAndSubProjectBulletins` fills it too —
       * so replacing it from one project's read would throw away every
       * sub-project row that loader had put there.
       */
      .addCase(refreshBulletins.fulfilled, (state, action) => {
        bulletinsAdapter.setMany(state.bulletins, action.payload);
      })
      /**
       * Put a bulletin that a background write changed in front of the user.
       *
       * Dispatched once the refetch above has landed the row, so nothing here
       * touches data: it is only the question of whether to move the view, and
       * each guard is a case where moving it would be wrong rather than merely
       * pointless.
       *
       * `project` holds whichever project the scene loaded and is emptied when
       * it unmounts, so it is the board answering whether it is on screen and on
       * this project. Off screen there is no view to move — and the next mount
       * opens the newest bulletin, which is the created one anyway.
       *
       * An `updated` never changes the selection. Repainting the bulletin the
       * user is reading is the whole point of it; an edit to one they are not
       * reading is no reason to take away the one they are. A `created` does
       * take the pane, which is what makes the assistant's link optional.
       *
       * Every other op does nothing, and that list is closed on purpose. It
       * used to be open — anything that was not an `updated` fell through to
       * the create path — which is how the first op added after this was
       * written, `deleted`, opened the bulletin it had just removed. An op this
       * client has never heard of is not a reason to move the user's view.
       */
      .addCase(bulletinActions.revealBulletin, (state, action) => {
        const { id, op, projectId } = action.payload ?? {};
        const bulletin = state.bulletins.entities[id];

        if (!bulletin || !state.project.entities[projectId]) return;

        const takesThePane =
          op === 'created' ||
          (op === 'updated' && state.bulletins.bulletinDialogData?.id === id);

        if (!takesThePane) return;

        // Copied out of the draft rather than aliased into it: every other
        // writer of this key puts a plain row here, and a bulletin is flat.
        state.bulletins.bulletinDialogData = { ...bulletin };
        state.lastOpenedBulletinId = id;
      })
      /**
       * A bulletin a background write deleted is gone from the list.
       *
       * Unconditional, where revealing is not: whether to move the view is a
       * question only the board can answer, but whether a row still exists is
       * not a question at all. It runs off screen and on another project too,
       * so the next visit does not open with a bulletin that is not there.
       *
       * Clearing the pane belongs to the removal rather than being a second
       * decision — nothing may be left showing a row that no longer exists —
       * and the board's own effect then opens the next one for a user who is
       * looking at the list.
       */
      .addCase(bulletinActions.removeBulletin, (state, action) => {
        const id = action.payload;

        if (!id) return;

        bulletinsAdapter.removeOne(state.bulletins, id);

        // `null` rather than `{}`: the board renders the pane whenever this is
        // truthy, and an empty object is a `<Bulletin>` with no bulletin.
        if (state.bulletins.bulletinDialogData?.id === id) {
          state.bulletins.bulletinDialogData = null;
        }

        if (state.lastOpenedBulletinId === id) {
          state.lastOpenedBulletinId = null;
        }
      })
      /**
       * A bulletin drafted elsewhere — by Nerobotti — for the user to edit and
       * save.
       *
       * Kept first, opened second, and the second is conditional the way
       * `revealBulletin` is: only the board can say whether it is showing that
       * project, and off screen there is no editor to open. A draft the board
       * cannot open now waits in `pendingBulletinDraft` for the mount that can
       * — asked for from the dashboard, it is in the editor when the user
       * arrives at the board — and the reply that produced it carries a link
       * there.
       *
       * Last writer wins. Two drafts at once would be two answers to "what did
       * I ask for", and the newer question is the one the user is waiting on.
       */
      .addCase(bulletinActions.receiveBulletinDraft, (state, action) => {
        const draft = action.payload;

        if (!draft?.projectId) return;

        state.pendingBulletinDraft = draft;
        openPendingDraftIfShowing(state, draft.projectId);
      })
      /**
       * The board claiming the draft waiting for it, once its project has
       * loaded — or once the edit dialog that was in the way has closed.
       *
       * A draft older than `PENDING_DRAFT_TTL_MS` is dropped instead. It was
       * written about a week that may since have moved on, and an editor
       * springing open on a board visited an hour later would be a surprise
       * rather than the thing the user asked for.
       */
      .addCase(bulletinActions.openPendingBulletinDraft, (state, action) => {
        const { projectId, now } = action.payload ?? {};
        const draft = state.pendingBulletinDraft;

        if (!draft || !projectId || draft.projectId !== projectId) return;

        if (
          typeof now === 'number' &&
          typeof draft.receivedAt === 'number' &&
          now - draft.receivedAt > PENDING_DRAFT_TTL_MS
        ) {
          state.pendingBulletinDraft = null;
          return;
        }

        openPendingDraftIfShowing(state, projectId);
      })
      /**
       * The user, offered a draft over an editor holding something else,
       * choosing the draft. Their text goes; they said so.
       */
      .addCase(bulletinActions.acceptPendingBulletinDraft, (state) => {
        const draft = state.pendingBulletinDraft;

        if (!draft || !state.project.entities[draft.projectId]) return;

        takeEditor(state, draft);
      })
      /** The same offer, declined. The reply still has the text. */
      .addCase(bulletinActions.discardPendingBulletinDraft, (state) => {
        state.pendingBulletinDraft = null;
      })
      /**
       * The create editor reporting what it holds. Every change, because the
       * check against it is equality — see `revisesWhatEditorHolds`.
       */
      .addCase(bulletinActions.setComposingBulletin, (state, action) => {
        state.bulletins.composing = action.payload ?? null;
      })
      .addCase(loadInitialBulletinData.rejected, (state, action) => ({
        ...state,
        loading: false,
        error: action.error,
      }))
      .addCase(loadAttachments.pending, (state) => ({
        ...state,
        loadingAttachments: true,
      }))
      .addCase(loadAttachments.fulfilled, (state, action) => {
        state.loadingAttachments = false;
        attachmentsAdapter.setMany(state.attachments, action.payload);
      })
      .addCase(loadAttachments.rejected, (state, action) => ({
        ...state,
        loadingAttachments: false,
        loadingAttachmentsError: action.error,
      }))
      .addCase(deleteAttachment.pending, (state) => ({
        ...state,
        deleteAttachmentLoading: true,
      }))
      .addCase(deleteAttachment.fulfilled, (state, action) => {
        state.deleteAttachmentLoading = false;
        attachmentsAdapter.removeOne(
          state.attachments,
          action.payload.deletedAttachment.id
        );
      })
      .addCase(deleteAttachment.rejected, (state, action) => ({
        ...state,
        deleteAttachmentLoading: false,
        deleteAttachmentError: action.error,
      }))
      .addCase(bulletinActions.addAttachments, (state, action) => {
        attachmentsAdapter.addMany(state.attachments, action.payload);
      })
      .addCase(loadProjectAndSubProjectBulletins.fulfilled, (state, action) => {
        bulletinsAdapter.setMany(state.bulletins, action.payload);
      })
      .addCase(bulletinActions.openBulletinDialog, (state, action) =>
        action.payload
          ? {
              ...state,
              bulletins: {
                ...state.bulletins,
                bulletinDialogData: action.payload,
              },
            }
          : {
              ...state,
              bulletins: {
                ...state.bulletins,
                bulletinDialogData: {},
              },
            }
      )
      .addCase(bulletinActions.closeBulletinDialog, (state, action) => ({
        ...state,
        bulletins: {
          ...state.bulletins,
          bulletinDialogData: action.payload,
        },
      }))
      .addCase(bulletinActions.resetBulletinData, (state, action) => ({
        ...state,
        ...resetState,
      }))
      // The user's own "Luo uusi": a blank editor, whatever draft was open
      // in the one before.
      .addCase(bulletinActions.openCreateBulletin, (state, action) => ({
        ...state,
        bulletins: {
          ...state.bulletins,
          createBulletin: true,
          bulletinDialogData: null,
          draft: null,
        },
      }))
      // Closing the editor is the end of the draft, saved or not: it came
      // from a conversation, and the next one can ask again. A draft the
      // editor was offering goes with it — closing is declining — while one
      // waiting for another project's board is none of this editor's business.
      .addCase(bulletinActions.closeCreateBulletin, (state) => {
        state.bulletins.createBulletin = false;
        state.bulletins.draft = null;
        state.bulletins.composing = null;

        const pending = state.pendingBulletinDraft;

        if (pending && state.project.entities[pending.projectId]) {
          state.pendingBulletinDraft = null;
        }
      })
      .addCase(createBulletin.pending, (state) => ({
        ...state,
        createBulletinLoading: true,
      }))
      .addCase(createBulletin.fulfilled, (state, action) => {
        state.createBulletinLoading = false;
        const bulletins = [
          action.payload.createdBulletin,
          ...selectAllInsideSlice(state),
        ];
        bulletinsAdapter.setAll(state.bulletins, bulletins);
        state.bulletins.bulletinDialogData = action.payload.createdBulletin;
        state.lastOpenedBulletinId = action.payload.createdBulletin.id;
      })
      .addCase(createBulletin.rejected, (state, action) => ({
        ...state,
        createBulletinLoading: false,
        createBulletinError: action.error,
      }))
      .addCase(loadImageSelectorData.pending, (state) => ({
        ...state,
        loadingFilePermissions: true,
      }))
      .addCase(loadImageSelectorData.fulfilled, (state, action) => {
        state.loadingFilePermissions = false;
        filePermissionsAdapter.addMany(
          state.filePermissions,
          action.payload.filePermissions
        );
      })
      .addCase(loadImageSelectorData.rejected, (state, action) => ({
        ...state,
        loadingFilePermissions: false,
        error: action.error,
      }))
      .addCase(bulletinActions.openImageSelector, (state, action) => ({
        ...state,
        bulletins: {
          ...state.bulletins,
          imageSelector: true,
        },
      }))
      .addCase(bulletinActions.closeImageSelector, (state, action) => ({
        ...state,
        bulletins: {
          ...state.bulletins,
          imageSelector: false,
        },
      }))
      .addCase(bulletinActions.openEditBulletin, (state, action) => ({
        ...state,
        bulletins: {
          ...state.bulletins,
          editBulletin: true,
          editableBulletin: action.payload,
        },
      }))
      .addCase(bulletinActions.closeEditBulletin, (state, action) => ({
        ...state,
        bulletins: {
          ...state.bulletins,
          editBulletin: false,
          editableBulletin: null,
        },
      }))
      .addCase(
        bulletinActions.openJoiningInstructionDialog,
        (state, action) => ({
          ...state,
          bulletins: {
            ...state.bulletins,
            joiningInstructionDialog: true,
          },
        })
      )
      .addCase(
        bulletinActions.closeJoiningInstructionDialog,
        (state, action) => ({
          ...state,
          bulletins: {
            ...state.bulletins,
            joiningInstructionDialog: false,
          },
        })
      )
      .addCase(editBulletin.pending, (state) => ({
        ...state,
        editBulletinLoading: true,
      }))
      .addCase(editBulletin.fulfilled, (state, action) => {
        state.editBulletinLoading = false;
        bulletinsAdapter.upsertOne(
          state.bulletins,
          action.payload.editedBulletin
        );
        state.bulletins = {
          ...state.bulletins,
          bulletinDialogData: action.payload.editedBulletin,
        };
        state.lastOpenedBulletinId = action.payload.editedBulletin.id;
      })
      .addCase(editBulletin.rejected, (state, action) => ({
        ...state,
        editBulletinLoading: false,
        editBulletinError: action.error,
      }))
      .addCase(bulletinActions.openDeleteBulletinDialog, (state, action) => ({
        ...state,
        bulletins: {
          ...state.bulletins,
          deleteBulletinDialog: true,
          deleteBulletinData: action.payload,
        },
      }))
      .addCase(bulletinActions.closeDeleteBulletinDialog, (state, action) => ({
        ...state,
        bulletins: {
          ...state.bulletins,
          deleteBulletinDialog: false,
          deleteBulletinData: null,
        },
      }))
      .addCase(deleteBulletin.pending, (state) => ({
        ...state,
        deleteBulletinLoading: true,
      }))
      .addCase(deleteBulletin.fulfilled, (state, action) => {
        state.deleteBulletinLoading = false;
        bulletinsAdapter.removeOne(
          state.bulletins,
          action.payload.deletedBulletin.id
        );
      })
      .addCase(deleteBulletin.rejected, (state, action) => ({
        ...state,
        deleteBulletinLoading: false,
        deleteBulletinError: action.error,
      }))
      .addCase(loadBulletinSendData.pending, (state) => ({
        ...state,
        bulletins: {
          ...state.bulletins,
          loadingBulletinSendData: true,
        },
      }))
      .addCase(loadBulletinSendData.fulfilled, (state, action) => {
        state.bulletins.loadingBulletinSendData = false;
        resourcesAdapter.setMany(state.resources, action.payload.resources);
        manualResourcesAdapter.setMany(
          state.manualResources,
          action.payload.manualResources
        );
      })
      .addCase(loadBulletinSendData.rejected, (state, action) => ({
        ...state,
        bulletins: {
          ...state.bulletins,
          loadingBulletinSendData: false,
        },
        loading: false,
        error: action.error,
      }))
      .addCase(bulletinActions.openSendBulletin, (state, action) => ({
        ...state,
        bulletins: {
          ...state.bulletins,
          sendBulletinDialog: true,
          sendBulletinData: action.payload,
        },
      }))
      .addCase(bulletinActions.closeSendBulletin, (state, action) => ({
        ...state,
        bulletins: {
          ...state.bulletins,
          sendBulletinDialog: false,
          sendBulletinData: null,
        },
      }))
      .addCase(sendBulletin.pending, (state) => ({
        ...state,
        sendBulletinLoading: true,
      }))
      .addCase(sendBulletin.fulfilled, (state, action) => {
        state.sendBulletinLoading = false;
        state.loadingPdf = false;
        state.sendBulletinError = action.payload.response.error;
      })
      .addCase(sendBulletin.rejected, (state, action) => {
        state.sendBulletinLoading = false;
        state.loadingPdf = false;
        state.sendBulletinError = action.payload.response.error;
      })
      .addCase(sendJoiningInstruction.pending, (state) => ({
        ...state,
        sendJoiningInstructionLoading: true,
      }))
      .addCase(sendJoiningInstruction.fulfilled, (state, action) => {
        state.sendJoiningInstructionLoading = false;
        state.sendJoiningInstructionError = action.payload.response.error;
      })
      .addCase(sendJoiningInstruction.rejected, (state, action) => {
        state.sendJoiningInstructionLoading = false;
        state.sendJoiningInstructionError = action.payload.response.error;
      })
      .addCase(bulletinActions.addFilepermissionUrl, (state, action) => {
        state.filepermissionUrls = [
          ...state.filepermissionUrls,
          action.payload,
        ];
      })
      .addCase(bulletinActions.resetFilepermissionUrls, (state) => {
        state.filepermissionUrls = [];
      })
      .addCase(bulletinActions.setLoadingPdf, (state, action) => {
        state.loadingPdf = action.payload;
      })
      .addCase(bulletinActions.openAddEmailDialog, (state, action) => ({
        ...state,
        bulletins: {
          ...state.bulletins,
          addEmailDialog: true,
        },
      }))
      .addCase(bulletinActions.closeAddEmailDialog, (state, action) => ({
        ...state,
        bulletins: {
          ...state.bulletins,
          addEmailDialog: false,
        },
      }))
      .addCase(createManualResource.pending, (state) => ({
        ...state,
        createManualResourceLoading: true,
      }))
      .addCase(createManualResource.fulfilled, (state, action) => {
        state.createManualResourceLoading = false;
        manualResourcesAdapter.addOne(
          state.manualResources,
          action.payload.createdManualResource
        );
      })
      .addCase(createManualResource.rejected, (state, action) => ({
        ...state,
        createManualResourceLoading: false,
        createManualResourceError: action.error,
      }))
      .addCase(bulletinActions.setBulletinImageUrls, (state, action) => ({
        ...state,
        imageUrls: action.payload,
      }))
      .addCase(bulletinActions.setLastOpenedBulletinId, (state, action) => ({
        ...state,
        lastOpenedBulletinId: action.payload,
      }))
      .addCase(
        bulletinActions.setOpenAttachmentDeleteDialog,
        (state, action) => ({
          ...state,
          openAttachmentDeleteDialog: action.payload,
        })
      )
      .addCase(
        bulletinActions.openEditRecipientResourceDialog,
        (state, action) => {
          state.selectedRecipientResourceToEditId = action.payload;
        }
      )
      .addCase(
        bulletinActions.closeEditRecipientResourceDialog,
        (state, action) => {
          state.selectedRecipientResourceToEditId = '';
        }
      )
      .addCase(updateManualResource.fulfilled, (state, action) => {
        manualResourcesAdapter.upsertOne(state.manualResources, action.payload);
        state.selectedRecipientResourceToEditId = '';
        enqueueSnackbar('Vastaanottajan tiedot päivitetty', {
          variant: 'success',
        });
      })
      .addCase(updateResidentResource.fulfilled, (state, action) => {
        resourcesAdapter.upsertOne(state.resources, action.payload);
        state.selectedRecipientResourceToEditId = '';
        enqueueSnackbar('Vastaanottajan tiedot päivitetty', {
          variant: 'success',
        });
      });
  },
});

export const {
  selectAll: selectAllResources,
  selectEntities: selectResourceEntities,
  selectById: selectResourceById,
  selectIds: selectResourceIds,
} = resourcesAdapter.getSelectors((state) => state.bulletinBoard.resources);

export const {
  selectAll: selectAllManualResources,
  selectEntities: selectManualResourcesEntities,
} = manualResourcesAdapter.getSelectors(
  (state) => state.bulletinBoard.manualResources
);

export const {
  selectAll: selectAllBulletins,
  selectEntities: selectBulletinEntities,
  selectById: selectBulletinById,
  selectIds: selectBulletinIds,
} = bulletinsAdapter.getSelectors((state) => state.bulletinBoard.bulletins);

// This is to avoid undefined error in the usage differences between slice and selectors
export const { selectAll: selectAllInsideSlice } =
  bulletinsAdapter.getSelectors((state) => state.bulletins);

export const { selectAll: selectAllDefaultFilePermissions } =
  defaultFilePermissionsAdapter.getSelectors(
    (state) => state.bulletinBoard.defaultFilePermissions
  );

export const selectSelectedRecipientResourceToEditId = (state) =>
  state.bulletinBoard.selectedRecipientResourceToEditId;

export default bulletinBoardSlice.reducer;
